import Text from "@tiptap/extension-text";
import { createEditor } from "../wysiwyg/tiptap/solid/createEditor";
import { EditorContent } from "../wysiwyg/tiptap/solid/EditorContent";
import type { ExtendableInputProps } from "./Input";
import Input from "./Input";
import type { Accessor } from "solid-js";
import { createEffect, createMemo, on, untrack } from "solid-js";
import { InputButton } from "../button/Button";
import { useTranslation } from "../../state/i18n";
import { initPlaceholder } from "../utils/placeholder/placeholder";
import type { EditorView } from "@tiptap/pm/view";
import withErrorBoundary from "../../utils/withErrorBoundary";
import { fromTipTapContent, toTipTapContent } from "../../utils/placeholderUtil";
import { History } from "@tiptap/extension-history";
import type { Delimiter, StringTemplate } from "../../utils/stringTemplateUtil";
import { isNil } from "lodash-es";
import type { DynamicValueId, SelectOptions } from "../../types/Menu";

export type MixedInputProperties = {
  value: StringTemplate;
  parse: (value: string) => StringTemplate;
  onChange: (value: StringTemplate) => void;
  options: SelectOptions<DynamicValueId>;
  placeholder?: string;
  delimiter?: Delimiter;
} & ExtendableInputProps;

const MixedInput = (props: MixedInputProperties) => {
  const { t } = useTranslation();
  const hasError = () => !isNil(props.errorMessage);
  const delimiter: Accessor<Delimiter> = createMemo(() => props.delimiter ?? "${");
  const options = createMemo(() => props.options);
  const placeholder = createMemo(() => initPlaceholder(() => editor, delimiter, options, "block"));

  const content = createMemo(() => toTipTapContent(props.value, placeholder().options(), t));

  const editor = createEditor({
    extensions: [
      Text,
      History,
      // oxlint-disable-next-line solid/reactivity
      ...placeholder().extensions,
    ],
    content: untrack(() => content()),
    onBlur: ({ editor }) => {
      const content = editor.getJSON().content ?? [];
      const hasInputComponent = content.some((node) => node.type === "inputComponent");
      if (!hasInputComponent) {
        props.onChange(fromTipTapContent(content));
      }
    },
    editorProps: {
      transformPastedText: (text: string, _plain: boolean, view: EditorView) => {
        view.pasteHTML(
          toTipTapContent(
            props.parse(text),
            untrack(() => placeholder().options()),
            t,
          ),
        );
        return "";
      },
      transformPastedHTML: (value: string) =>
        toTipTapContent(
          props.parse(value),
          untrack(() => placeholder().options()),
          t,
        ),
      attributes: {
        class:
          "relative min-h-9 text-base-regular w-full rounded-sm pl-2 py-2 outline-none pr-10 wrap-break-word whitespace-break-spaces",
        role: "textbox",
        "aria-label": untrack(() => props.label),
        tabIndex: "0",
      },
    },
  });

  // Reset the editor content when options are changed (this automatically results in pills to update when a field is
  // deleted or changes it's label)
  createEffect(
    on(
      () => content(),
      (value) => editor()?.commands.setContent(value),
    ),
  );

  return (
    <Input required={props.required} errorMessage={props.errorMessage} info={props.info} label={props.label}>
      {() => (
        <div
          class="relative rounded-sm border text-base-regular text-primary open:outline-default has-[div[role='textbox']:focus-visible]:outline-default"
          classList={{
            "border-default bg-surface-default": !hasError(),
            "border-error bg-surface-destructive": hasError(),
          }}
        >
          <EditorContent editor={editor()} />
          <InputButton
            aria-label={t(($) => $.MIXED_INPUT_ADD_BUTTON_ARIA_LABEL)}
            icon="lightningBolt"
            classList={{ "absolute top-1 right-1": true }}
            onClick={placeholder().addPlaceholder}
            data-placeholder-button
          />
        </div>
      )}
    </Input>
  );
};

const MixedInputWithErrorBoundary = withErrorBoundary(MixedInput);

export default MixedInputWithErrorBoundary;
